import { extractRequestScopePath } from '@terra/shared-asset-processing';
import { DomainSourceSchema } from '@terra/shared-schemas-orchestrator';
import {
  hasAllowlistPathRules,
  resolveDomainTestingScope,
} from './domain-config-sync.util';
import type {
  DomainConfigInputSource,
  DomainConfigPathRuleInput,
} from './domain-config-input.util';
import { normalizeScopePathForComparison } from './domain-validation';

const normalizeApplicationUrl = (applicationUrl: string): string => {
  const trimmed = applicationUrl.trim();

  return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
};

export const getHostnameFromApplicationUrl = (
  applicationUrl: string,
): string | null => {
  try {
    return new URL(normalizeApplicationUrl(applicationUrl)).hostname || null;
  } catch {
    return null;
  }
};

export const getHostnameFromDomainValue = (
  domainValue: string,
): string | null => {
  const trimmed = domainValue.trim();

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      return new URL(trimmed).hostname || null;
    } catch {
      return null;
    }
  }

  return trimmed.split('/')[0]?.toLowerCase() ?? null;
};

const isAllowlistRule = (rule: DomainConfigPathRuleInput): boolean =>
  rule.scope !== 'BLOCKLIST';

export const getPrimaryHostEntryPathRule = (
  pathRules: DomainConfigPathRuleInput[] | undefined,
): DomainConfigPathRuleInput | undefined =>
  (pathRules ?? []).find((rule) => isAllowlistRule(rule));

export const getPrimaryHostExtraPathRules = (
  pathRules: DomainConfigPathRuleInput[] | undefined,
): DomainConfigPathRuleInput[] => {
  const rules = pathRules ?? [];
  let entrySkipped = false;

  return rules.filter((rule) => {
    if (!entrySkipped && isAllowlistRule(rule)) {
      entrySkipped = true;

      return false;
    }

    return true;
  });
};

export const extractEntryPathFromApplicationUrl = (
  applicationUrl: string,
): string | null => {
  try {
    const scopePath = extractRequestScopePath(
      normalizeApplicationUrl(applicationUrl),
    );

    if (!scopePath || scopePath === '/') {
      return null;
    }

    return normalizeScopePathForComparison(scopePath);
  } catch {
    return null;
  }
};

export const splitPrimaryHostPathRules = ({
  applicationUrl,
  pathRules,
}: {
  applicationUrl: string;
  pathRules: DomainConfigPathRuleInput[] | undefined;
}): {
  entryPathRule: DomainConfigPathRuleInput | undefined;
  extraPathRules: DomainConfigPathRuleInput[];
} => {
  const rules = pathRules ?? [];
  const urlEntryPath = extractEntryPathFromApplicationUrl(applicationUrl);

  if (!urlEntryPath) {
    return { entryPathRule: undefined, extraPathRules: rules };
  }

  const normalizedUrlEntryPath = normalizeScopePathForComparison(urlEntryPath);

  const entryPathRuleIndex = rules.findIndex(
    (rule) =>
      rule.scope !== 'BLOCKLIST' &&
      normalizeScopePathForComparison(rule.path) === normalizedUrlEntryPath,
  );

  if (entryPathRuleIndex === -1) {
    return { entryPathRule: undefined, extraPathRules: rules };
  }

  return {
    entryPathRule: rules[entryPathRuleIndex],
    extraPathRules: rules.filter((_, index) => index !== entryPathRuleIndex),
  };
};

const buildEntryPathRule = ({
  path,
  isInTestingScope,
}: {
  path: string;
  isInTestingScope: boolean;
}): DomainConfigPathRuleInput => ({
  path,
  scope: 'ALLOWLIST',
  isInFunctionalityScope: true,
  isInTestingScope,
  isThirdParty: false,
});

export type BuildApplicationDomainsParams = {
  applicationUrl: string;
  primaryHostAttackEnabled?: boolean;
  primaryHostExtraPathRules?: DomainConfigPathRuleInput[];
  additionalDomains: DomainConfigInputSource[];
  existingPrimaryHostEntry?: DomainConfigInputSource;
  syncEntryPathFromApplicationUrl?: boolean;
};

export const buildApplicationDomains = ({
  applicationUrl,
  primaryHostAttackEnabled = true,
  primaryHostExtraPathRules = [],
  additionalDomains,
  existingPrimaryHostEntry,
  syncEntryPathFromApplicationUrl = false,
}: BuildApplicationDomainsParams): DomainConfigInputSource[] => {
  const hostname = getHostnameFromApplicationUrl(applicationUrl);

  if (!hostname) {
    return additionalDomains;
  }

  const entryPath = syncEntryPathFromApplicationUrl
    ? extractEntryPathFromApplicationUrl(applicationUrl)
    : null;
  const entryPathRule = entryPath
    ? buildEntryPathRule({
        path: entryPath,
        isInTestingScope: primaryHostAttackEnabled,
      })
    : null;

  const pathRules = [
    ...(entryPathRule ? [entryPathRule] : []),
    ...primaryHostExtraPathRules,
  ];

  const nonPrimaryDomains = additionalDomains.filter((entry) => {
    const entryHostname = getHostnameFromDomainValue(entry.domain);

    return entryHostname?.toLowerCase() !== hostname.toLowerCase();
  });

  const usesAllowlistPartialScope = hasAllowlistPathRules(pathRules);

  const primaryHostEntry: DomainConfigInputSource = {
    ...(existingPrimaryHostEntry ?? {
      source: DomainSourceSchema.enum.USER_PROVIDED,
      hasWaf: null,
      createdAt: new Date(),
      enabledAt: primaryHostAttackEnabled ? new Date() : null,
    }),
    domain: hostname,
    pathRules,
    isThirdParty: false,
    ...(usesAllowlistPartialScope
      ? {
          isInFunctionalityScope:
            existingPrimaryHostEntry?.isInFunctionalityScope ?? null,
          isInTestingScope: null,
        }
      : {
          isInFunctionalityScope: true,
          isInTestingScope: primaryHostAttackEnabled,
        }),
  };

  return [...nonPrimaryDomains, primaryHostEntry];
};

export const splitPrimaryHostScopeFromDomains = ({
  applicationUrl,
  domains,
}: {
  applicationUrl: string;
  domains: DomainConfigInputSource[];
}): {
  primaryHostAttackEnabled: boolean;
  primaryHostExtraPathRules: DomainConfigPathRuleInput[];
  additionalDomains: DomainConfigInputSource[];
  existingPrimaryHostEntry?: DomainConfigInputSource;
} => {
  const hostname = getHostnameFromApplicationUrl(applicationUrl);

  if (!hostname) {
    return {
      primaryHostAttackEnabled: true,
      primaryHostExtraPathRules: [],
      additionalDomains: domains,
    };
  }

  const primaryHostEntry = domains.find((entry) => {
    const entryHostname = getHostnameFromDomainValue(entry.domain);

    return entryHostname?.toLowerCase() === hostname.toLowerCase();
  });

  const additionalDomains = domains.filter((entry) => {
    const entryHostname = getHostnameFromDomainValue(entry.domain);

    return entryHostname?.toLowerCase() !== hostname.toLowerCase();
  });

  const { entryPathRule, extraPathRules: primaryHostExtraPathRules } =
    splitPrimaryHostPathRules({
      applicationUrl,
      pathRules: primaryHostEntry?.pathRules,
    });

  return {
    primaryHostAttackEnabled: entryPathRule
      ? entryPathRule.isInTestingScope
      : primaryHostEntry
        ? resolveDomainTestingScope(primaryHostEntry)
        : true,
    primaryHostExtraPathRules,
    additionalDomains,
    existingPrimaryHostEntry: primaryHostEntry,
  };
};

export const buildApplicationDomainsFromScope = ({
  applicationUrl,
  domains,
  primaryHostAttackEnabled,
  previousApplicationUrl,
  previousRedirectToUrl,
  previousDomains = [],
}: {
  applicationUrl: string;
  domains: DomainConfigInputSource[];
  primaryHostAttackEnabled?: boolean;
  previousApplicationUrl?: string;
  previousRedirectToUrl?: string | null;
  previousDomains?: DomainConfigInputSource[];
}): DomainConfigInputSource[] => {
  const previousHostname = previousApplicationUrl
    ? getHostnameFromApplicationUrl(previousApplicationUrl)
    : null;
  const previousRedirectHostname = previousRedirectToUrl
    ? getHostnameFromApplicationUrl(previousRedirectToUrl)
    : null;
  const currentHostname = getHostnameFromApplicationUrl(applicationUrl);
  const staleHostnames =
    previousHostname &&
    currentHostname &&
    previousHostname.toLowerCase() !== currentHostname.toLowerCase()
      ? new Set(
          [previousHostname, previousRedirectHostname]
            .filter((hostname): hostname is string => Boolean(hostname))
            .map((hostname) => hostname.toLowerCase()),
        )
      : null;
  const domainsForSplit = staleHostnames
    ? domains.filter((entry) => {
        const entryHostname = getHostnameFromDomainValue(entry.domain);

        return (
          !entryHostname || !staleHostnames.has(entryHostname.toLowerCase())
        );
      })
    : domains;

  const split = splitPrimaryHostScopeFromDomains({
    applicationUrl,
    domains: domainsForSplit,
  });
  const previousPrimarySplit = staleHostnames
    ? undefined
    : splitPrimaryHostScopeFromDomains({
        applicationUrl,
        domains: previousDomains,
      });
  const previousPrimaryHostEntry = previousPrimarySplit?.existingPrimaryHostEntry;
  const existingPrimaryHostEntry =
    split.existingPrimaryHostEntry ?? previousPrimaryHostEntry;

  const shouldSyncEntryPathFromApplicationUrl =
    Boolean(previousApplicationUrl) ||
    (domainsForSplit.length === 0 &&
      extractEntryPathFromApplicationUrl(applicationUrl) !== null);

  return buildApplicationDomains({
    applicationUrl,
    primaryHostAttackEnabled:
      primaryHostAttackEnabled ??
      (split.existingPrimaryHostEntry
        ? split.primaryHostAttackEnabled
        : (previousPrimarySplit?.primaryHostAttackEnabled ??
          split.primaryHostAttackEnabled)),
    primaryHostExtraPathRules: shouldSyncEntryPathFromApplicationUrl
      ? split.primaryHostExtraPathRules
      : (existingPrimaryHostEntry?.pathRules ?? []),
    additionalDomains: split.additionalDomains,
    existingPrimaryHostEntry,
    syncEntryPathFromApplicationUrl: shouldSyncEntryPathFromApplicationUrl,
  });
};
